import * as React from "react";
import {
    PlasmicCanvasView,
    DefaultCanvasViewProps
} from "./plasmic/main/PlasmicCanvasView";
import {useCallback, useEffect, useState} from "react";
import {Note, Position, Project} from "../models/fauna/dbSchema";
import {
    createNote,
    createProject,
    deleteNote,
    getProject,
    getProjectNotes, getSharedNote,
    updateNote,
    updateProject
} from "../api/fetch-requests";
import ListElement from "./ListElement";
import ModelCanvas from "./ModelCanvas";
import {useLinkClickHandler} from "react-router-dom";
import {BuildingStorey, IFCSpace} from "../models/types";
import {toast, ToastContainer} from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import DemoModel from "./DemoModel";
import {DefaultXRControllers, Hands, VRCanvas} from "@react-three/xr";
import Control from "./Control";
import {Environment} from "@react-three/drei";
import {ErrorBoundary} from "react-error-boundary";
import Metaverse from "./Metaverse";

export interface CanvasViewProps extends DefaultCanvasViewProps {
    projectID?: string,
    noteID?: string,
    user: any
}

export type CanvasStates =
    "noteList"
    | "noteEditor"
    | "settings"
    | "levels"
    | "waitList"
    | "spaces"
    | "googleDrivePreview";
const exclusiveCanvasStates = ["settings", "levels", "waitList", "spaces", "googleDrivePreview"];
const demoURL = './court-transformed.glb';
const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);

const defaultProject: Project = {id: 'local', name: 'Untitled', status: "notStored"};


function CanvasView({projectID, noteID, user, ...props}: CanvasViewProps) {
    const [project, setProject] = useState<Project>();
    const [notes, setNotes] = useState<any[]>([]);
    const [file, setFile] = useState<File | undefined>();
    const [displayedNote, setDisplayedNote] = useState<Note | undefined>();

    const goBack = useLinkClickHandler(`/`);

    const [googleDriveSheet, setGoogleDriveSheet] = useState<any>();

    const [buildingStories, setBuildingStories] = useState<BuildingStorey[]>([]);
    const [displayedStory, setDisplayedStory] = useState<BuildingStorey>();

    const [IFCSpaces, setIFCSpaces] = useState<IFCSpace[]>([]);
    const [highlightedIDs, setHighlightedIDs] = useState<any[]>([]);
    const [highlightedSpace, setHighlightedSpace] = useState<IFCSpace>();

    const [canvasState, setCanvasState] = useState<CanvasStates[]>([]);

    const [defaultPosition, sDP] = useState<Position>({x: 0, y: 2, z: 0});
    const [defaultLook, sDL] = useState<Position>({x: 0, y: 2, z: 0});

    const [cameraLook, setCameraLook] = useState<Position>(defaultLook);
    const [cameraPosition, setCameraPosition] = useState<Position>(defaultPosition);

    const setDefaultPosition = useCallback((defaultPosition: Position) => {
        sDP(defaultPosition);
        setCameraPosition(defaultPosition);
    }, []);
    const setDefaultLook = useCallback((defaultLook: Position) => {
        sDL(defaultLook);
        setCameraLook(defaultLook);
    }, []);

    const openEditor = useCallback((note: Note) => {
        setDisplayedNote(note);
        setCanvasState((state) => [...state, 'noteEditor']);
    }, []);

    const closeEditor = useCallback(() => {
        setCanvasState((state) => state.filter((s) => s === "noteEditor"));
        setDisplayedNote(undefined);
    }, []);

    const jumpTo = useCallback((lookPosition: Position, cameraPosition: Position) => {
        setCameraPosition({x: cameraPosition.x, y: cameraPosition.y, z: cameraPosition.z});
        setCameraLook({x: lookPosition.x, y: lookPosition.y, z: lookPosition.z});
    }, []);

    const addNote = useCallback((note: any) => {
        if (project?.status === 'uploading') return
        if (project?.id === 'local') {
            const newNote = {...note, local: true, id: s4()};
            setNotes(notes => [...notes, newNote]);
            openEditor(newNote);
        } else if (project?.id) {
            const waitingInterval = setInterval(() => document.body.style.cursor = 'wait', 500);
            createNote(project.id, note).then((note) => {
                clearInterval(waitingInterval);
                document.body.style.cursor = 'auto';
                setNotes(notes => [...notes, note]);
                openEditor(note);
            }).catch((err) => {
                console.error(err);
                toast.error(err, {autoClose: false});
                clearInterval(waitingInterval);
                document.body.style.cursor = 'auto';
            });
        }
    }, [project, openEditor]);

    const uploadNotes = useCallback(async (id: string) => {
        const uploaded_notes: any[] = [];
        for (let i = 0; i < notes.length; i++) {
            uploaded_notes.push(await createNote(id, notes[i]));
        }
        setNotes(uploaded_notes);
    }, [notes]);

    const removeNote = useCallback((note: Note) => {
        if (!note.local) {
            deleteNote(note.id).catch((err) => {
                console.error(err);
                toast.error(err, {autoClose: false});
            });
        }
        closeEditor();
        setNotes(notes => notes.filter((n) => n.id !== note.id));
    }, [closeEditor]);

    const updateNoteChanges = useCallback((note: Note, noteChanges: any) => {
        if (!note.local) {
            updateNote(note.id, noteChanges).catch((err) => {
                console.error(err);
                toast.error(err, {autoClose: false});
            });
        }
        const updatedNote = {...note, ...noteChanges};
        setNotes(notes => notes.map((n) =>
            (n.id === note.id ? updatedNote : n))
        );
        setDisplayedNote(prevState => prevState?.id === note.id ? updatedNote : prevState);
    }, []);

    const jumpAndEditNote = useCallback((note: Note) => {
        openEditor(note);
        jumpTo(note.position, note.cameraPosition);
    }, [jumpTo, openEditor]);

    const viewFile = useCallback(() => {
        let input = document.createElement('input');
        input.type = 'file';
        input.onchange = () => {
            if (input?.files?.length && input?.files?.length > 0) {
                const file: File = input.files[0];
                setFile(file);
                const exts = /[^.]+$/.exec(file.name);
                const ext = exts && exts.length > 0 ? exts[0] : undefined;
                if ((ext === 'gltf' || ext === 'glb' || ext === 'obj' || ext === 'fbx' || ext === 'ifc') && ext && file) {
                    const url = URL.createObjectURL(file)
                    setProject({...defaultProject, ext, url});
                } else {
                    console.log("No file in the right format registered");
                    toast.error("File format not supported", {autoClose: false});
                }
            }
        };
        input.click();
    }, []);

    const uploadProject = useCallback(({id, url, ...project}: Project) => {
        if (user && url) {
            setProject((prevState) => {
                return prevState ? {...prevState, status: 'uploading'} : undefined;
            });
            createProject(project, file).then((id) => {
                uploadNotes(id).then(() => {
                    setProject((prevState) => {
                        return prevState ? {...prevState, id, status: 'uploaded'} : undefined;
                    });
                });
            }).catch((err) => {
                console.error(err);
                toast.error(err, {autoClose: false});

            });
        } else if (!user) {
            setCanvasState((state) => [...state, "waitList"]);
        }
    }, [file, uploadNotes, user]);

    const createDemoProject = useCallback(() => {
        const url = demoURL;
        const exts = /[^.]+$/.exec(demoURL);
        const ext = exts && exts.length > 0 ? exts[0] : undefined;
        setProject({...defaultProject, ext, name: "Demo Project", url});
    }, []);

    const showBuildingStories = useCallback((buildingStoreys: BuildingStorey[]) => {
        setBuildingStories(buildingStoreys);
    }, []);

    const showIFCSpaces = useCallback((ifcSpaces: IFCSpace[]) => {
        setIFCSpaces(ifcSpaces);
    }, []);

    const filterExclusiveStates = useCallback(
        (state: CanvasStates[]) => {
            return state.filter((s: CanvasStates) => !exclusiveCanvasStates.includes(s))
        },
        []);

    useEffect(() => {
        !canvasState.includes("noteEditor") && setDisplayedNote(undefined);
    }, [canvasState]);

    const showGoogleDriveSheet = useCallback((url: string) => {
        setGoogleDriveSheet(url);
        setCanvasState(prevState => [...filterExclusiveStates(prevState), "googleDrivePreview"]);
    }, [filterExclusiveStates]);

    // There can be either a project be displayed or a note
    useEffect(() => {
        if (projectID) {
            getProject(projectID).then((project) => {
                if (!project.url) {
                    setProject({...project, url: demoURL});
                } else {
                    setProject(project);
                }
            }).catch((err) => {
                console.error(err);
                toast.error(err, {autoClose: false});
            });
            getProjectNotes(projectID).then(setNotes).catch((err) => {
                console.error(err);
                toast.error(err, {autoClose: false});
            });
        } else if (noteID && noteID !== '1' && noteID !== '2') {
            getSharedNote(noteID).then((note) => {
                setNotes([note]);
                setProject({name: "Shared", url: note.url, ext: note.ext, status: "uploaded", id: "readonly"});
                note.storey && setDisplayedStory(note.storey);
                setDefaultLook(note.position);
                setDefaultPosition(note.cameraPosition);
                openEditor(note);
            }).catch((err) => {
                console.error(err);
                toast.error(err, {autoClose: false});
            });
        }
    }, [noteID, openEditor, projectID, setDefaultLook, setDefaultPosition]);

    return (noteID !== '1' && noteID !== '2' ? <PlasmicCanvasView
        state={canvasState}
        noFile={!projectID && !noteID && !project?.url}
        toProjects={{
            onClick: (e: any) => goBack(e)
        }}
        fileUpload={{
            onClick: viewFile
        }}
        createDemo={{
            onClick: createDemoProject
        }}
        projectTitleEditor={{
            entry: project,
            readonly: !!noteID,
            onChange: (project) => {
                if (project.id !== 'local') {
                    updateProject(project.id, {name: project.name}).catch((err) => {
                        console.error(err);
                        toast.error(err, {autoClose: false});
                    });
                }
                setProject({...project, name: project.name});
            },
            onUpload: uploadProject
        }}
        noteViewer={{
            displayedNote: displayedNote,
            onChange: !noteID ? updateNoteChanges : undefined,
            onClose: closeEditor,
            onDelete: removeNote,
            jumpTo: jumpTo,
            showGoogleDriveSheet: showGoogleDriveSheet
        }}
        googleDriveSheet={{
            as: "iframe",
            props: {
                src: googleDriveSheet,
                title: "GoogleDrive"
            }

        }}
        projectSettings={{
            project: project,
            onClose: () => setCanvasState(filterExclusiveStates(canvasState)),
            onChange: (projectChanges: any) => {
                if (user && project) {
                    updateProject(project.id, projectChanges).catch((err) => {
                        console.error(err);
                        toast.error(err, {autoClose: false});
                    });
                    setProject({...project, ...projectChanges});
                }
            }
        }}
        noFileOptions={{
            onDrag: (e) => {
                e.preventDefault()
                console.log(e);
            }
        }}
        grayMenuArea={{
            onDrag: (e) => {
                e.preventDefault()
                console.log(e)
            },
            onClick: () => setCanvasState(filterExclusiveStates(canvasState))
        }}
        showLevel={!!displayedStory}
        levelDisplay={{
            text: displayedStory?.name ? displayedStory.name : '',
            action: () => setCanvasState(state => [...state, "levels"])
        }}
        showSpace={!!highlightedSpace}
        spaceDisplay={{
            text: highlightedSpace?.name ? highlightedSpace.name : '',
            action: () => setCanvasState(state => [...state, "spaces"])
        }}
        levelsMenu={{
            onClose: () => setCanvasState(filterExclusiveStates(canvasState)),
            empty: canvasState.includes("levels") ? !buildingStories.length : !IFCSpaces.length,
            display: canvasState.includes("levels") ? "levels" : "spaces",
            storiesList: canvasState.includes("levels") ?
                buildingStories.map((buildingStorey: BuildingStorey, index) => <ListElement
                    key={buildingStorey.name + buildingStorey.elevation + index}
                    name={buildingStorey.name}
                    action={() => {
                        setDisplayedStory(prevState => prevState === buildingStorey ? undefined : buildingStorey);
                        setCanvasState(filterExclusiveStates(canvasState));
                    }}
                    selected={displayedStory === buildingStorey}
                />) :
                IFCSpaces.map((space: IFCSpace, index) => <ListElement
                    key={space.name + index}
                    name={space.name}
                    action={() => {
                        setHighlightedSpace(prevState => prevState === space ? undefined : space);
                        setHighlightedIDs((highlightedIDs) => {
                            if (!highlightedIDs.includes(space.expressID)) {
                                return [space.expressID];
                            } else {
                                return [];
                            }
                        });
                        setCanvasState(filterExclusiveStates(canvasState));
                    }}
                    selected={highlightedIDs.includes(space.expressID)}
                />)
        }}
        canvasSidebar={{
            canvasState: canvasState,
            readonly: !!noteID,
            showNoteList: (b) => setCanvasState(
                (state) => b ?
                    [...state, "noteList"] :
                    state.filter((s) => s !== "noteList")),
            showSettings: (b) => setCanvasState(
                (state) => b ?
                    [...filterExclusiveStates(state), "settings"] :
                    state.filter((s) => s !== "settings")),
            showRooms: (b) => setCanvasState(
                (state) => b ?
                    [...filterExclusiveStates(state), "spaces"] :
                    state.filter((s) => s !== "spaces")),
            jumpToDefaultPos: () => jumpTo(defaultLook, defaultPosition),
            showLevels: (b) => setCanvasState(
                (state) => b ?
                    [...filterExclusiveStates(state), "levels"] :
                    state.filter((s) => s !== "levels"))
        }}
        noteList={{
            notes: notes,
            jumpAndEditNote: jumpAndEditNote,
            displayedNote: displayedNote
        }}
        toastify={{
            render: () => (<ToastContainer/>)
        }}
        canvasDiv={{
            render: (props, Component) => (
                <Component {...props} >
                    <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[file]} onError={(err) => {
                        setProject(defaultProject);
                        setFile(undefined);
                        toast.error(
                            "We can not read the content of this file. Please check if the file has the right extension.",
                            {autoClose: false});
                    }}>
                        <ModelCanvas
                            notes={notes}
                            displayedNote={displayedNote}
                            displayOneNote={!!noteID}
                            viewNote={openEditor}
                            cameraLook={cameraLook}
                            cameraPosition={cameraPosition}
                            setDefaultPosition={setDefaultPosition}
                            setDefaultLook={setDefaultLook}
                            url={project?.url}
                            ext={project?.ext}
                            displayedStory={displayedStory}
                            showBuildingStories={showBuildingStories}
                            highlightedIDs={highlightedIDs}
                            showIFCSpaces={showIFCSpaces}
                            addNote={addNote}
                        />
                    </ErrorBoundary>
                </Component>
            )
        }}
        {...props}
    >
    </PlasmicCanvasView> : noteID === '1' ? <Demo/> : <MetaverseDemo/>);
}

function ErrorFallback() {
    return <></>
}

function Demo() {
    return <VRCanvas frameloop="demand">
        <DemoModel/>
        <Control lookAt={undefined} position={undefined}/>
        <Environment
            files="https://vazxmixjsiawhamofees.supabase.co/storage/v1/object/public/hdris/noon-grass/noon_grass_1k.hdr"
            background/>
        <DefaultXRControllers
            /** Optional material props to pass to controllers' ray indicators */
            rayMaterial={{color: 'blue'}}
            /** Whether to hide controllers' rays on blur. Default is `false` */
            hideRaysOnBlur={false}
        />
        <Hands/>
    </VRCanvas>
}

function MetaverseDemo() {
    return <VRCanvas frameloop="demand">
        <Metaverse/>
        <Control lookAt={undefined} position={undefined}/>
        <DefaultXRControllers
            /** Optional material props to pass to controllers' ray indicators */
            rayMaterial={{color: 'blue'}}
            /** Whether to hide controllers' rays on blur. Default is `false` */
            hideRaysOnBlur={false}
        />
        <Hands/>
    </VRCanvas>
}


export default CanvasView;
